import type { IBountyService } from "@/services/interfaces/IBountyService";
import { isKnownCurrency } from "@/lib/types";
import type {
  Attachment,
  BountyCard,
  BountyDetail,
  BountyStatus,
  DiscoverParams,
  InterestedBounty,
  PaginatedResult,
  WorkMode,
  WorkSubmission,
} from "@/lib/types";
import { SCENE_TO_CATEGORY, deriveQuestScene } from "@/lib/questArt/scene";
import type { ContentI18n } from "@/lib/questContent/types";
import { supabase } from "@/lib/supabase";
import { mapPostedAsOrg, POSTED_AS_ORG_EMBED, type PostedAsOrgRow } from "./orgMapping";
import { getChainConfig } from "@/config/contracts";
import type { PolygonClient } from "@/lib/polygon";
import { getContractErrorName } from "@/lib/polygon";
import { assertOnActiveChain } from "@/lib/chainGuard";
import { EscrowFrozenError, EscrowNotReleasableError } from "@/services/errors";
import { isTrustGradeOverridden, type TaskTrustRow } from "@/lib/trustGrade/mapRow";
import { sanitizeTrustFactors } from "@/lib/trustGrade/rubric";
import { gradeRange } from "@/lib/trustGrade/range";
import type { TrustGrade } from "@/lib/trustGrade/types";
import { payRangeFromBudgets, sortByPayRange } from "@/lib/bounty/payRange";

/**
 * `EscrowVault.EscrowStatus` (protocol/src/EscrowVault.sol:17).
 *
 * 4 is Disputed: a dispute is open on some task of the quest, and
 * `releasePayment` reverts `EscrowFrozen()` for ALL of them.
 * `PolygonPaymentService` maps the same value to the `disputed` record status.
 *
 * It is not the only status that cannot pay, though. The vault accepts ONLY
 * Active and PartiallyReleased and reverts the very same `EscrowFrozen()` for
 * FullyReleased and Refunded. The releasable set is therefore stated as an
 * allowlist rather than a list of blocked values, so a status added by a
 * future vault fails closed instead of sliding through as releasable.
 */
const ESCROW_STATUS_DISPUTED = 4;
const ESCROW_STATUS_RELEASABLE = new Set([
  0, // Active
  1, // PartiallyReleased
]);

// ─── Row types ───────────────────────────────────────────

interface TaskWithQuestRow extends TaskTrustRow {
  id: string;
  quest_id: string;
  title: string;
  description: string;
  task_type: string;
  status: string;
  budget_usd: number;
  estimated_effort_hours: number;
  deadline: string | null;
  skills: string[];
  assignee_id: string | null;
  order: number;
  chain_task_id: number | null;
  updated_at?: string | null;
  i18n: ContentI18n | null;
  source_lang: string | null;
  // Durable-proof backstop (DEV-workspace-chat-media): the submission's own
  // `attachments` column, embedded so a failed submission-message send never
  // makes uploaded proof unrecoverable. Optional/array because a task can
  // have zero (not yet submitted) or several (resubmission after changes
  // requested) submissions; only the most recent one's attachments are used.
  submissions?: Array<{ attachments: unknown; submitted_at: string }> | null;
  quests: {
    id: string;
    title: string;
    category: string;
    currency: string;
    creator_id: string;
    created_at: string;
    i18n: ContentI18n | null;
    source_lang: string | null;
    profiles: {
      display_name: string;
      bio: string | null;
      avatar_url: string | null;
    } | null;
    organization?: PostedAsOrgRow | null;
    quest_templates: {
      max_slots: number;
      filled_slots: number;
      status: string;
    } | null;
  };
}

interface TaskRow extends TaskTrustRow {
  id: string;
  title: string;
  description: string;
  task_type: string;
  status: string;
  budget_usd: number;
  estimated_effort_hours: number;
  deadline: string | null;
  skills: string[];
  assignee_id: string | null;
  order: number;
  i18n: ContentI18n | null;
  source_lang: string | null;
  work_mode: string;
  location_countries: string[];
  location_cities: string[];
}

/**
 * One row per QUEST for the discover feed, with its open tasks embedded. The
 * feed used to select from `tasks`, which turned a 10-task quest into 10
 * near-identical cards; keying the card off the quest and counting the open
 * tasks as slots is what collapses them back into one.
 */
interface QuestWithTasksRow {
  id: string;
  title: string;
  description: string;
  category: string;
  currency: string;
  creator_id: string;
  total_budget_usd: number;
  deadline: string | null;
  created_at: string;
  i18n: ContentI18n | null;
  source_lang: string | null;
  profiles: {
    display_name: string;
    bio: string | null;
    avatar_url: string | null;
  } | null;
  organization?: PostedAsOrgRow | null;
  quest_templates: {
    max_slots: number;
    filled_slots: number;
    status: string;
  } | null;
  tasks: TaskRow[];
}

// Durable-proof backstop (I1): `submissions.attachments` is JSONB and the
// only writer is submitWork below — be defensive, never throw, on malformed
// or legacy ([]) rows. Mirrors the inverse mapping in submitWork.
function mapStoredAttachment(stored: unknown): Attachment | null {
  if (!stored || typeof stored !== "object") return null;
  const rec = stored as Record<string, unknown>;
  const path = rec.path;
  if (typeof path !== "string" || path.length === 0) return null;
  return {
    id: path,
    fileName: typeof rec.name === "string" && rec.name ? rec.name : path,
    url: path,
    fileType: typeof rec.mime === "string" ? rec.mime : "application/octet-stream",
    sizeBytes: typeof rec.size === "number" ? rec.size : 0,
  };
}

function latestSubmissionAttachments(
  submissions: TaskWithQuestRow["submissions"],
): Attachment[] {
  if (!Array.isArray(submissions) || submissions.length === 0) return [];
  const latest = submissions.reduce((a, b) =>
    new Date(a?.submitted_at ?? 0).getTime() >= new Date(b?.submitted_at ?? 0).getTime() ? a : b,
  );
  if (!Array.isArray(latest?.attachments)) return [];
  return latest.attachments
    .map(mapStoredAttachment)
    .filter((a): a is Attachment => a !== null);
}

/** The newest submission's time, for My Work's "Submitted" event. */
function latestSubmittedAt(submissions: TaskWithQuestRow["submissions"]): string | null {
  if (!Array.isArray(submissions)) return null;
  return submissions.reduce<string | null>(
    (latest, s) => (s?.submitted_at && (!latest || s.submitted_at > latest) ? s.submitted_at : latest),
    null,
  );
}

function mapToBountyCard(row: TaskWithQuestRow): BountyCard {
  // Derived once, here, because this is the only place holding the RAW column
  // and the task's skills together. The scene is the richer value and the
  // category is its narrowing, so resolving the scene first and mapping down
  // keeps the art and the filter from ever disagreeing.
  const artScene = deriveQuestScene(row.quests.category, row.skills ?? []);

  return {
    artScene,
    trustGrade: (row.trust_grade ?? null) as TrustGrade | null,
    trustGradeLeast: (row.trust_grade ?? null) as TrustGrade | null,
    // Only selects that ask for these columns (select *) carry them; a narrower
    // list select leaves both undefined rather than claiming "no factors".
    trustFactors: row.trust_factors === undefined ? undefined : sanitizeTrustFactors(row.trust_factors),
    trustGradeOverridden: row.trust_grade_override === undefined ? undefined : isTrustGradeOverridden(row),
    id: row.id,
    taskTitle: row.title,
    taskDescription: row.description,
    taskType: row.task_type as BountyCard["taskType"],
    questTitle: row.quests.title,
    questId: row.quests.id,
    // Canonical filter category, DERIVED — never the raw column. Every quest
    // created through the app has `quests.category = 'general'` (hardcoded in
    // AgentBountyBridge) and the column's own default is '', so keying art off
    // the raw value rendered the same `other` scene on every card in the feed.
    // `?? "Other"` did not help: it only catches NULL, not '' or 'general'.
    // The task's skills are the honest per-quest signal, and using them fixes
    // quests created before this shipped without a backfill.
    category: SCENE_TO_CATEGORY[artScene],
    budgetUSD: Number(row.budget_usd),
    currency: isKnownCurrency(row.quests.currency) ? row.quests.currency : "USDT",
    estimatedEffortHours: Number(row.estimated_effort_hours),
    deadline: row.deadline,
    questCreatedAt: row.quests.created_at ?? null,
    skills: row.skills ?? [],
    // Empty-string display_name is real in this table: 20260208000003
    // backfilled placeholder profiles with '' to satisfy the new FKs. Treat
    // it as absent, exactly like a missing row.
    creatorName: row.quests.profiles?.display_name?.trim() || null,
    postedAsOrg: mapPostedAsOrg(row.quests.organization),
    // DEV-781: the card's asker line. Null (not "") when the profile has no
    // bio, so buildQuestCard omits the line instead of rendering a dangling
    // separator. Read via the `public_profiles` view, not the `profiles` base
    // table: base-table SELECT is owner-or-admin (20260413000003), so embedding
    // it returned null for every quest the viewer did not own — a silent 200,
    // which is what hid this behind the name fallback above. The
    // `!fk_quests_creator` hint is required, not decorative: quests also has
    // details_verified_by -> profiles(id), so an unhinted embed is ambiguous.
    creatorBlurb: row.quests.profiles?.bio?.trim() || null,
    creatorAvatarUrl: row.quests.profiles?.avatar_url ?? null,
    creatorId: row.quests.creator_id ?? null,
    i18n: row.i18n ?? {},
    sourceLang: row.source_lang ?? null,
    questI18n: row.quests.i18n ?? {},
    questSourceLang: row.quests.source_lang ?? null,
    applicantCount: 0,
    maxSlots: row.quests.quest_templates?.max_slots ?? null,
    filledSlots: row.quests.quest_templates?.filled_slots ?? null,
  };
}

function mapQuestToBountyCard(row: QuestWithTasksRow): BountyCard {
  const openTasks = row.tasks.filter((t) => t.status === "open");
  // `tasks!inner` guarantees >=1 task; the fallback only keeps TS happy.
  const firstTask = openTasks.sort((a, b) => a.order - b.order)[0] ?? row.tasks[0]!;
  const allSkills = [...new Set(openTasks.flatMap((t) => t.skills ?? []))];

  // ── What ONE worker earns, not what the creator escrows ─────────────────
  // This card stands for N DIFFERENT jobs under one escrow, and whoever reads
  // it takes exactly one of them. `quests.total_budget_usd` is therefore the
  // wrong number by a factor of N: on a 100+200+300 quest it rendered one
  // task's title, "3 tasks open", and "You'll be paid 600 USDT".
  //
  // Priced from the OPEN tasks only — a sibling already assigned is not on
  // offer, and including it quotes a job nobody reading this can take. The
  // fallback mirrors `firstTask`'s: `tasks!inner` guarantees a row, and an
  // all-assigned quest is filtered out upstream by `.eq("tasks.status","open")`.
  const { budgetUSD, budgetMaxUSD } = payRangeFromBudgets(
    openTasks.length > 0
      ? openTasks.map((t) => Number(t.budget_usd))
      : [Number(firstTask.budget_usd)],
  )!; // Never empty: the fallback above always supplies `firstTask`.

  // Derived, never the raw column — see mapToBountyCard for why the raw
  // `quests.category` cannot be trusted. The skills of the open tasks are the
  // honest per-quest signal, and the scene narrows down to the filter category.
  const artScene = deriveQuestScene(row.category, allSkills);

  const range = gradeRange(openTasks.map((t) => (t.trust_grade ?? null) as TrustGrade | null));

  return {
    artScene,
    trustGrade: range?.most ?? null,
    trustGradeLeast: range?.least ?? null,
    id: firstTask.id, // first open task — the detail route still keys off a task
    taskTitle: firstTask.title,
    taskDescription: firstTask.description || row.description,
    taskType: firstTask.task_type as BountyCard["taskType"],
    questTitle: row.title,
    questId: row.id,
    category: SCENE_TO_CATEGORY[artScene],
    budgetUSD,
    budgetMaxUSD,
    currency: isKnownCurrency(row.currency) ? row.currency : "USDT",
    estimatedEffortHours: openTasks.reduce(
      (sum, t) => sum + Number(t.estimated_effort_hours),
      0,
    ),
    deadline: row.deadline,
    questCreatedAt: row.created_at,
    skills: allSkills,
    creatorName: row.profiles?.display_name?.trim() || null,
    postedAsOrg: mapPostedAsOrg(row.organization),
    creatorBlurb: row.profiles?.bio?.trim() || null,
    creatorAvatarUrl: row.profiles?.avatar_url ?? null,
    creatorId: row.creator_id ?? null,
    // The card headline is the QUEST title, so the quest's translations are
    // the ones that matter; the task's are carried for the description.
    i18n: firstTask.i18n ?? {},
    sourceLang: firstTask.source_lang ?? null,
    questI18n: row.i18n ?? {},
    questSourceLang: row.source_lang ?? null,
    applicantCount: 0,
    maxSlots: row.quest_templates?.max_slots ?? null,
    filledSlots: row.quest_templates?.filled_slots ?? null,
    openTaskCount: openTasks.length,
    openTaskIds: openTasks.map((t) => t.id),
    workMode: deriveStrictestWorkMode(openTasks),
    locationCountries: [...new Set(openTasks.flatMap((t) => t.location_countries ?? []))],
    locationCities: [...new Set(openTasks.flatMap((t) => t.location_cities ?? []))],
  };
}

const WORK_MODE_RANK: Record<string, number> = { onsite: 2, hybrid: 1, remote: 0 };
function deriveStrictestWorkMode(tasks: TaskRow[]): WorkMode {
  return tasks.reduce<WorkMode>(
    (acc, t) => ((WORK_MODE_RANK[t.work_mode] ?? 0) > (WORK_MODE_RANK[acc] ?? 0) ? (t.work_mode as WorkMode) : acc),
    "remote",
  );
}

// ─── Service ─────────────────────────────────────────────

export class SupabaseBountyService implements IBountyService {
  constructor(private polygonClient: PolygonClient | null = null) {}

  async getDiscoverFeed(
    params: DiscoverParams,
  ): Promise<PaginatedResult<BountyCard>> {
    const limit = params.limit ?? 20;
    const chainId = getChainConfig().chainId;

    // Query quests with their open tasks joined.
    // `tasks!inner` ensures we only get quests that have matching tasks.
    // Filtering `tasks.status = open` restricts to quests with open slots.
    let query = supabase
      .from("quests")
      .select(
        [
          "id, title, description, category, currency, creator_id, total_budget_usd, deadline, created_at, i18n, source_lang",
          // `public_profiles`, not `profiles`: base-table SELECT is
          // owner-or-admin, so an embed of it returns null for every quest the
          // viewer does not own — a silent 200. The `!fk_quests_creator` hint
          // is required because quests also has details_verified_by -> profiles.
          "profiles:public_profiles!fk_quests_creator(display_name, bio, avatar_url)",
          // Directory columns only: anon holds a column grant on exactly these
          // (20261007100000), and one ungranted column fails the whole query.
          POSTED_AS_ORG_EMBED,
          "quest_templates:template_id(max_slots, filled_slots, status)",
          "tasks!inner(id, title, description, task_type, status, budget_usd, estimated_effort_hours, deadline, skills, assignee_id, order, i18n, source_lang, trust_grade, work_mode, location_countries, location_cities)",
        ].join(", "),
        { count: "exact" },
      )
      .in("status", ["active", "published", "negotiating"])
      .eq("chain_id", chainId)
      .eq("tasks.status", "open")
      .limit(limit);

    // Task-type filter on the joined tasks
    if (params.taskType) {
      query = query.eq("tasks.task_type", params.taskType);
    }

    // Exclude own quests
    if (params.userId) {
      query = query.neq("creator_id", params.userId);
    }

    // Budget filter, server-side. Applied after `.limit` only ever meant
    // fetching a page and then throwing part of it away — and shrinking the
    // page is what made `hasMore` lie.
    //
    // Keyed on the TASK's pay, not `quests.total_budget_usd`. The worker is
    // asking "show me jobs that pay 50–150", and the quest total is the sum
    // across every task: it hid a 100 job inside a 600 quest and surfaced a
    // 600 quest whose every task pays 200. Filtering the embedded rows is the
    // same mechanism `tasks.status` and `tasks.task_type` already use — with
    // `tasks!inner` it both selects the quest and narrows the embed, so the
    // card's own pay range and open-task count describe the tasks that matched.
    if (params.budgetMin !== undefined) {
      query = query.gte("tasks.budget_usd", params.budgetMin);
    }
    if (params.budgetMax !== undefined) {
      query = query.lte("tasks.budget_usd", params.budgetMax);
    }

    // Location filter, on the joined tasks' location columns. `cs` is the
    // PostgREST "contains" operator: `location_countries.cs.{PY}` matches any
    // row whose array includes 'PY'. Same mechanism as skills/categories.
    if (params.locationCountry) {
      query = query.contains("tasks.location_countries", [params.locationCountry]);
    }
    if (params.locationCity) {
      query = query.contains("tasks.location_cities", [params.locationCity]);
    }
    // Work-mode filter: "remote" is the default, but a worker asking for
    // onsite-only work genuinely wants only tasks that require presence.
    if (params.workMode) {
      query = query.eq("tasks.work_mode", params.workMode);
    }

    // Text search (on quest title/description)
    if (params.search) {
      query = query.or(
        `title.ilike.%${params.search}%,description.ilike.%${params.search}%`,
      );
    }

    // Sort
    switch (params.sortBy) {
      // Ranked by what a WORKER earns, matching the filter above. The quest
      // total is the sum across every task, so ordering by it put a 3x100
      // quest above a single 250 job under "highest paying". PostgREST cannot
      // order a parent row by an embedded column, which is why these are
      // derived columns on `quests` maintained by trigger (20260918120200)
      // rather than an aggregate expressed here. `nullsFirst: false` keeps a
      // quest with nothing open out of the head of the ranking.
      case "budget_desc":
        query = query.order("max_open_task_budget_usd", {
          ascending: false,
          nullsFirst: false,
        });
        break;
      case "budget_asc":
        query = query.order("min_open_task_budget_usd", {
          ascending: true,
          nullsFirst: false,
        });
        break;
      case "deadline_asc":
        query = query.order("deadline", { ascending: true, nullsFirst: false });
        break;
      case "newest":
      default:
        query = query.order("created_at", { ascending: false });
        break;
    }

    // Cursor-based pagination (quest IDs)
    if (params.cursor) {
      query = query.lt("id", params.cursor);
    }

    const { data, error, count } = await query;
    if (error)
      throw new Error(`Failed to fetch discover feed: ${error.message}`);

    let results = (data as unknown as QuestWithTasksRow[]).map(
      mapQuestToBountyCard,
    );

    // Swipe exclusion: hide quests where all open tasks have been swiped
    if (params.userId) {
      const { data: swipedRows } = await supabase
        .from("swipe_history")
        .select("task_id")
        .eq("user_id", params.userId);

      const swipedIds = new Set((swipedRows ?? []).map((r) => r.task_id));
      if (swipedIds.size > 0) {
        // What each open task pays, so a card can be re-priced from the tasks
        // that survive the exclusion. Read off the rows rather than carried on
        // the card: a per-task pay map is not something any view renders.
        const payByTaskId = new Map<string, number>();
        // Same for the trust range: "Easy–Expert" must not keep quoting the Expert job
        // the reader already swiped away.
        const gradeByTaskId = new Map<string, TrustGrade | null>();
        for (const row of data as unknown as QuestWithTasksRow[]) {
          for (const task of row.tasks) {
            if (task.status !== "open") continue;
            payByTaskId.set(task.id, Number(task.budget_usd));
            gradeByTaskId.set(task.id, (task.trust_grade ?? null) as TrustGrade | null);
          }
        }

        // The pay range has to be RE-priced, not just the count corrected.
        // `budgetUSD`/`budgetMaxUSD` were derived from the pre-exclusion task
        // set, so a reader who swiped the 100 job away kept seeing
        // "100–300 USDT" on a card where only the 300 is left — the card
        // quoting a job nobody reading it can take, which is the exact defect
        // the range was added to remove.
        results = results
          .map((card) => {
            const remaining = (card.openTaskIds ?? []).filter(
              (id) => !swipedIds.has(id),
            );
            const repriced = payRangeFromBudgets(
              remaining
                .map((id) => payByTaskId.get(id))
                .filter((value): value is number => typeof value === "number"),
            );
            const regraded = gradeRange(remaining.map((id) => gradeByTaskId.get(id) ?? null));
            return {
              ...card,
              openTaskCount: remaining.length,
              openTaskIds: remaining,
              // null when no surviving id has a recorded pay — the card keeps
              // the figures it already had rather than being re-priced from
              // nothing.
              ...(repriced ?? {}),
              ...(regraded ? { trustGrade: regraded.most, trustGradeLeast: regraded.least } : {}),
            };
          })
          .filter((card) => (card.openTaskCount ?? 0) > 0);
      }
    }

    // Client-side skill filter. Compared case-insensitively: the UI sends
    // display casing ("React") while task skills may be stored differently.
    if (params.skills?.length) {
      const wanted = new Set(params.skills.map((s) => s.toLowerCase()));
      results = results.filter((b) =>
        b.skills.some((s) => wanted.has(s.toLowerCase())),
      );
    }

    // Client-side category filter. Compared case-insensitively: the filter UI
    // sends QUEST_CATEGORIES' display casing ("Development") while the card now
    // carries the canonical lowercase key. An exact `includes` could never
    // match — before this change it matched nothing either, since every row's
    // raw category was the literal 'general'.
    if (params.categories?.length) {
      const wanted = new Set(params.categories.map((c) => c.toLowerCase()));
      results = results.filter((b) => wanted.has(b.category.toLowerCase()));
    }

    // ── The ranking must agree with the figures on the cards ────────────────
    // Two things make the server's derived bounds disagree with what a card
    // shows, and both leave the feed ranked by a number the reader cannot see:
    //
    //   * The columns are computed over ALL of a quest's open tasks, while the
    //     query narrows the embed by `tasks.budget_usd`. Under `budgetMax=150`
    //     a quest whose matching task pays 100 but which also holds a 5000
    //     task outranked every quest whose matching task pays 150.
    //   * The swipe pass re-prices cards after the query has ordered them.
    //
    // Re-ordering by the card's own bounds is a correction, never a
    // corruption: with no budget filter and nothing swiped the two are the
    // same number, so this is a no-op. It is deliberately PAGE-scoped — the
    // ordering across pages is the server's, and reconciling that needs
    // keyset pagination on the ranking column (the cursor is still `id`),
    // which is a separate change.
    //
    // Which END of the range each direction ranks by is `sortByPayRange`'s
    // call, beside the rule that built the range in the first place.
    if (params.sortBy === "budget_desc" || params.sortBy === "budget_asc") {
      results = sortByPayRange(results, params.sortBy === "budget_asc" ? "asc" : "desc");
    }

    // `hasMore` must describe the QUERY, not the filtered view. Derived from
    // results.length it went false the moment a client-side filter (swipes,
    // skills, categories) dropped a single row — so infinite scroll stopped
    // early and the rest of the feed was unreachable. The raw row count is the
    // only thing that says whether the database had another page.
    const rawRows = data as unknown as QuestWithTasksRow[];
    const hasMore = rawRows.length === limit;
    const nextCursor = hasMore ? rawRows[rawRows.length - 1]?.id ?? null : null;

    return {
      data: results,
      total: count ?? results.length,
      cursor: nextCursor,
      hasMore,
    };
  }

  async swipeRight(bountyId: string, userId: string): Promise<void> {
    // Chain-first: express interest on-chain (MANDATORY for paid tasks).
    // Skipping the chain call would record a phantom swipe in DB while
    // BountyBoard never sees the worker, so the creator's applicants list
    // won't include them. Volunteer ($0) tasks have no on-chain presence at
    // all, so for them there is nothing to express interest against.
    const { isVolunteer, chainTaskId } = await this.loadTaskSettlement(bountyId);
    if (!isVolunteer) {
      if (!this.polygonClient) {
        throw new Error("Cannot express interest: wallet not connected. Please refresh and try again.");
      }
      if (chainTaskId === null) {
        throw new Error(
          "Cannot express interest: this task has not been funded on-chain yet. Please ask the quest creator to fund the quest, then try again.",
        );
      }
      await this.polygonClient.expressInterest(BigInt(chainTaskId));
    }

    // DB-second: record swipe
    const { error } = await supabase.from("swipe_history").insert({
      user_id: userId,
      task_id: bountyId,
      direction: "right",
    });
    if (error && !error.message.includes("duplicate")) {
      console.error("[SupabaseBountyService.swipeRight] DB insert failed:", error);
      throw new Error("Failed to record swipe. Please try again.");
    }
  }

  /**
   * Moved here from MyBounties.tsx, which ran this query inline: a component
   * naming a table and a column filter is the boundary this port exists to
   * hold. The chain filter is the same one `getDiscoverFeed` applies — a
   * candidacy on the other network is not this session's work.
   */
  async getMyInterests(userId: string): Promise<InterestedBounty[]> {
    const chainId = getChainConfig().chainId;
    const { data, error } = await supabase
      .from("swipe_history")
      .select(
        `
        task_id,
        created_at,
        tasks!inner (
          id,
          quest_id,
          title,
          status,
          budget_usd,
          i18n,
          source_lang,
          quests!inner (
            title,
            chain_id,
            currency,
            i18n,
            source_lang
          )
        )
      `,
      )
      .eq("user_id", userId)
      .eq("direction", "right")
      .eq("tasks.quests.chain_id", chainId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    if (!data) return [];

    return (data as unknown as Array<{
      task_id: string;
      created_at: string;
      tasks: {
        id: string;
        quest_id: string;
        title: string;
        status: string;
        budget_usd: number;
        i18n: ContentI18n | null;
        source_lang: string | null;
        quests: {
          title: string;
          currency: string;
          i18n: ContentI18n | null;
          source_lang: string | null;
        };
      };
    }>).map((row) => ({
      taskId: row.tasks.id,
      questId: row.tasks.quest_id,
      taskTitle: row.tasks.title,
      questTitle: row.tasks.quests.title,
      i18n: row.tasks.i18n ?? null,
      sourceLang: row.tasks.source_lang ?? null,
      questI18n: row.tasks.quests.i18n ?? null,
      questSourceLang: row.tasks.quests.source_lang ?? null,
      budgetUsd: Number(row.tasks.budget_usd),
      currency: isKnownCurrency(row.tasks.quests.currency)
        ? row.tasks.quests.currency
        : "USDT",
      status: row.tasks.status,
      appliedAt: row.created_at,
    }));
  }

  async swipeLeft(bountyId: string, userId: string): Promise<void> {
    const { error } = await supabase.from("swipe_history").insert({
      user_id: userId,
      task_id: bountyId,
      direction: "left",
    });
    if (error && !error.message.includes("duplicate")) {
      console.error("[SupabaseBountyService.swipeLeft] DB insert failed:", error);
      throw new Error("Failed to record swipe. Please try again.");
    }
  }

  async getBountyDetail(id: string): Promise<BountyDetail> {
    const { data, error } = await supabase
      .from("tasks")
      .select(
        `*, quests!inner(id, title, description, creator_id, created_at, i18n, source_lang, profiles:public_profiles!fk_quests_creator(display_name, bio, avatar_url), ${POSTED_AS_ORG_EMBED}, quest_templates:template_id(max_slots, filled_slots, status)), submissions(attachments, submitted_at)`,
      )
      .eq("id", id)
      .single();

    if (error) throw new Error(`Failed to fetch bounty detail: ${error.message}`);

    const row = data as unknown as TaskWithQuestRow;
    const card = mapToBountyCard(row);

    return {
      ...card,
      fullDescription: row.description,
      attachments: latestSubmissionAttachments(row.submissions),
      requirements: [],
      parentTaskContext: [],
      status: row.status as BountyStatus,
      assigneeId: row.assignee_id,
    };
  }

  async acceptBounty(
    bountyId: string,
    userId: string,
    options?: { onStepChange?: (stepId: string, status: "pending" | "active" | "done" | "error") => void },
  ): Promise<void> {
    const step = options?.onStepChange;

    // Chain-first: accept applicant on-chain (MANDATORY for paid tasks).
    // A volunteer ($0) task never went on-chain, so there is no applicant to
    // accept there; every other task must be assigned on-chain before the DB
    // records it, or the escrow can never pay the worker it names.
    const { isVolunteer, chainTaskId } = await this.loadTaskSettlement(bountyId);
    if (!isVolunteer) {
      if (!this.polygonClient) {
        throw new Error("Cannot accept applicant: wallet not connected. Please refresh and try again.");
      }
      if (chainTaskId === null) {
        throw new Error(
          "Cannot accept bounty: task is not yet funded on-chain. The quest creator must fund the quest first.",
        );
      }

      step?.("chain_assign", "active");

      // Look up applicant's wallet address via RPC (avoids PII exposure).
      // Distinguish RPC failure (transient DB / network) from "no wallet on file"
      // so a misleading onboarding-incomplete error isn't shown for a DB blip.
      const { data: wallet, error: walletErr } = await supabase.rpc("get_user_wallet", {
        p_user_id: userId,
      });

      if (walletErr) {
        throw new Error(`Failed to look up worker wallet: ${walletErr.message}`);
      }
      if (!wallet) {
        throw new Error("Worker has no wallet address. They need to log in with Privy to create an embedded wallet.");
      }

      await this.polygonClient.acceptApplicant(
        BigInt(chainTaskId),
        wallet,
      );
      step?.("chain_assign", "done");
    }

    // DB-second: update Supabase
    step?.("db_update", "active");
    const { error } = await supabase
      .from("tasks")
      .update({ assignee_id: userId, status: "assigned" })
      .eq("id", bountyId)
      .eq("status", "open");

    if (error) {
      console.error("[SupabaseBountyService.acceptBounty] DB update failed:", error);
      throw new Error("Failed to accept bounty. Please try again.");
    }
    step?.("db_update", "done");
  }

  async submitWork(
    bountyId: string,
    submission: WorkSubmission,
    options?: { onStepChange?: (stepId: string, status: "pending" | "active" | "done" | "error") => void },
  ): Promise<string> {
    const step = options?.onStepChange;

    // Volunteer (no chain steps) or paid (chain-first)? Fail-closed: a read
    // failure throws rather than defaulting to volunteer, which would skip
    // mandatory on-chain settlement for a paid task.
    const { isVolunteer, chainTaskId } = await this.loadTaskSettlement(bountyId);

    if (!isVolunteer) {
      // Chain-first: auto-start + submit work on-chain (MANDATORY for paid quests)
      if (!this.polygonClient) {
        throw new Error("Cannot submit work: wallet not connected. Please refresh and try again.");
      }
      if (chainTaskId === null) {
        throw new Error("Cannot submit work: this task has not been funded on-chain yet. Please ask the quest creator to fund the quest, then try again.");
      }

      step?.("chain_start", "active");
      try {
        await this.polygonClient.withContext(
          { flow: "userStartTask", taskId: chainTaskId },
          () => this.polygonClient!.startTask(BigInt(chainTaskId)),
        );
      } catch (err) {
        // InvalidStatus is expected when the task is already started; any
        // other revert (NotAssignee, NotRegistered, …) signals a real
        // problem and must surface so the user — and ops — see it.
        if (getContractErrorName(err) !== "InvalidStatus") throw err;
      }
      step?.("chain_start", "done");

      step?.("chain_submit", "active");
      await this.polygonClient.withContext(
        { flow: "userSubmittedWork", taskId: chainTaskId },
        () =>
          this.polygonClient!.submitWork(BigInt(chainTaskId), {
            description: submission.description,
            submitterId: submission.submitterId,
          }),
      );
      step?.("chain_submit", "done");
    }

    // DB: persist submission
    step?.("db_submit", "active");
    const { data, error } = await supabase.from("submissions").insert({
      task_id: bountyId,
      submitter_id: submission.submitterId,
      description: submission.description,
      status: "pending",
      // Proof of work, durable in the table of record — not only in the chat
      // message. `url` carries the storage path within the quest-files bucket.
      attachments: submission.attachments.map((a) => ({
        path: a.url,
        name: a.fileName,
        mime: a.fileType,
        size: a.sizeBytes,
      })),
    }).select("id").single();

    if (error) {
      console.error("[SupabaseBountyService.submitWork] DB insert failed after chain success:", error);
      throw new Error(isVolunteer
        ? "Failed to save your submission. Please try again."
        : "Submission saved on-chain but the database write failed. Please contact support.");
    }

    const persistedId = data.id as string;

    await supabase
      .from("tasks")
      .update({ status: "submitted" })
      .eq("id", bountyId);
    step?.("db_submit", "done");

    return persistedId;
  }

  async getMyBounties(
    userId: string,
    status?: BountyStatus,
  ): Promise<PaginatedResult<BountyDetail>> {
    const chainId = getChainConfig().chainId;
    let query = supabase
      .from("tasks")
      .select(
        `*, quests!inner(id, title, description, creator_id, created_at, chain_id, i18n, source_lang, profiles:public_profiles!fk_quests_creator(display_name, bio, avatar_url), ${POSTED_AS_ORG_EMBED}, quest_templates:template_id(max_slots, filled_slots, status)), submissions(attachments, submitted_at)`,
        { count: "exact" },
      )
      .eq("assignee_id", userId)
      .eq("quests.chain_id", chainId)
      .order("created_at", { ascending: false });

    if (status) {
      query = query.eq("status", status);
    }

    const { data, error, count } = await query;
    if (error)
      throw new Error(`Failed to fetch my bounties: ${error.message}`);

    const rows = data as unknown as TaskWithQuestRow[];
    const bounties: BountyDetail[] = rows.map((row) => {
      const card = mapToBountyCard(row);
      return {
        ...card,
        fullDescription: row.description,
        attachments: latestSubmissionAttachments(row.submissions),
        requirements: [],
        parentTaskContext: [],
        status: row.status as BountyStatus,
        assigneeId: row.assignee_id,
        submittedAt: latestSubmittedAt(row.submissions),
        updatedAt: row.updated_at ?? null,
      };
    });

    return {
      data: bounties,
      total: count ?? bounties.length,
      cursor: null,
      hasMore: false,
    };
  }

  /**
   * Refuse a fund-releasing action while a dispute holds this quest's escrow.
   *
   * `EscrowVault.EscrowStatus` 4 is Disputed; `PolygonPaymentService` maps it
   * to the `disputed` record status. A quest with no `chain_quest_id` has never
   * been on-chain and has no escrow to freeze.
   *
   * A dispute is not the only blocker: the vault releases only from Active and
   * PartiallyReleased, so a refunded or fully-released escrow reverts the same
   * way and is refused too — with a different error, because blaming a
   * non-existent dispute is worse than saying nothing.
   *
   * Fails CLOSED. An unreadable escrow must not read as "not frozen": the two
   * failure modes are not symmetric — refusing an approval the creator can
   * retry costs a click, while approving into a frozen vault burns gas and
   * leaves a worker marked approved and unpaid.
   */
  private async assertEscrowNotFrozen(questId: string): Promise<void> {
    if (!this.polygonClient) return;

    const { data: quest, error } = await supabase
      .from("quests")
      .select("chain_quest_id, chain_id")
      .eq("id", questId)
      .single();

    if (error) {
      throw new Error(`Cannot verify escrow state before approving: ${error.message}`);
    }
    if (!quest?.chain_quest_id) return;

    assertOnActiveChain(quest.chain_id, "quest");
    const escrow = await this.polygonClient.getEscrow(BigInt(quest.chain_quest_id));
    const status = Number(escrow.status);

    // A dispute gets its own error because it is the only one with a remedy
    // the reader can act on (resolve it) and a cause they cannot guess (it is
    // on a different task).
    if (status === ESCROW_STATUS_DISPUTED) {
      throw new EscrowFrozenError(questId);
    }
    // Everything else the vault refuses to release from — a refunded or
    // fully-released escrow — reverts identically, so it must be refused
    // identically, but must not be blamed on a dispute.
    if (!ESCROW_STATUS_RELEASABLE.has(status)) {
      throw new EscrowNotReleasableError(questId, status);
    }
  }

  async getPendingSubmissionId(taskId: string): Promise<string> {
    const { data, error } = await supabase
      .from("submissions")
      .select("id")
      .eq("task_id", taskId)
      .eq("status", "pending")
      .order("submitted_at", { ascending: false })
      .limit(1);
    if (error) throw new Error(`Failed to fetch submission: ${error.message}`);
    const id = data?.[0]?.id;
    if (!id) throw new Error("No pending submission found");
    return id;
  }

  async reviewSubmission(
    submissionId: string,
    decision: "approve" | "request_changes",
    feedback: string | undefined,
    expectedQuestId: string,
  ): Promise<void> {
    const newStatus =
      decision === "approve" ? "approved" : "changes_requested";

    // 1. Read-only: get task_id without mutating submission status
    const { data: submission, error: fetchErr } = await supabase
      .from("submissions")
      .select("task_id, status, tasks!inner(quest_id)")
      .eq("id", submissionId)
      .eq("status", "pending")
      .eq("tasks.quest_id", expectedQuestId)
      .single();

    if (fetchErr) {
      console.error("[SupabaseBountyService.reviewSubmission] Failed to fetch submission:", fetchErr);
      throw new Error("Failed to load submission. Please try again.");
    }

    // 2. Volunteer (skip chain) vs paid (chain-first MANDATORY). Fail-closed:
    // a read failure throws. Defaulting to volunteer here would skip
    // approveSubmission — and with it EscrowVault.releasePayment — leaving a
    // paid worker approved in the DB but never paid.
    const { isVolunteer, chainTaskId } = await this.loadTaskSettlement(submission.task_id);

    if (!isVolunteer) {
      if (!this.polygonClient) {
        throw new Error("Cannot review submission: wallet not connected. Please refresh and try again.");
      }
      if (chainTaskId === null) {
        throw new Error("Cannot review submission: this task has not been funded on-chain yet. Please ask the quest creator to fund the quest, then try again.");
      }

      if (decision === "approve") {
        // ── Preflight: is this quest's escrow frozen by someone else's dispute? ──
        // approveSubmission cascades to EscrowVault.releasePayment, which
        // reverts EscrowFrozen() while the escrow is Disputed — and the freeze
        // is QUEST-wide (DisputeResolver.fileDispute takes a taskId but calls
        // freezeEscrow(questId)). Without this read the creator sent the
        // transaction anyway, paid gas for a revert, and got no explanation
        // that the block came from a DIFFERENT task on the same quest.
        //
        // Read-only and approve-only: request_changes releases nothing, and
        // refusing it too would remove the one review action a dispute leaves
        // working.
        await this.assertEscrowNotFrozen(expectedQuestId);

        // approveSubmission auto-triggers EscrowVault.releasePayment via OPERATOR_ROLE
        await this.polygonClient.withContext(
          { flow: "creatorApprovedWork", taskId: chainTaskId },
          () => this.polygonClient!.approveSubmission(BigInt(chainTaskId)),
        );
      } else {
        await this.polygonClient.withContext(
          { flow: "creatorRequestedChanges", taskId: chainTaskId },
          () =>
            this.polygonClient!.requestChanges(BigInt(chainTaskId), {
              feedback: feedback ?? "",
            }),
        );
      }
    }

    // 3. DB-second: update submission status + task status AFTER chain success
    const { data: updatedSubmission, error: subUpdateErr } = await supabase
      .from("submissions")
      .update({ status: newStatus, feedback: feedback ?? null })
      .eq("id", submissionId)
      .eq("status", "pending")
      .select("id")
      .maybeSingle();

    if (subUpdateErr || !updatedSubmission) {
      console.error(`[reviewSubmission] ${isVolunteer ? "Volunteer" : "Chain succeeded but"} submission DB update failed. submissionId=${submissionId}, taskId=${submission.task_id}, decision=${decision}.`, subUpdateErr);
      throw new Error(isVolunteer
        ? "Failed to update submission. Please try again."
        : "On-chain action succeeded but the database update failed. Please contact support.");
    }

    const taskStatus =
      decision === "approve" ? "completed" : "changes_requested";
    const { error: taskUpdateErr } = await supabase
      .from("tasks")
      .update({ status: taskStatus })
      .eq("id", submission.task_id);

    if (taskUpdateErr) {
      console.error(`[reviewSubmission] ${isVolunteer ? "Volunteer" : "Chain succeeded but"} task DB update failed. submissionId=${submissionId}, taskId=${submission.task_id}, decision=${decision}.`, taskUpdateErr);
      throw new Error(isVolunteer
        ? "Failed to update task status. Please try again."
        : "On-chain action succeeded but the database update failed. Please contact support.");
    }
  }

  async expressInterestOnChain(
    taskId: string,
    options?: { onStepChange?: (stepId: string, status: "pending" | "active" | "done" | "error") => void },
  ): Promise<void> {
    const step = options?.onStepChange;
    if (!this.polygonClient) throw new Error("Polygon client not available");

    step?.("express_interest", "active");
    const chainTaskId = await this.lookupChainTaskId(taskId);
    if (chainTaskId === null) throw new Error("Task not yet on-chain");
    await this.polygonClient.withContext(
      { flow: "workerExpressedInterest", taskId: chainTaskId },
      () => this.polygonClient!.expressInterest(BigInt(chainTaskId)),
    );
    step?.("express_interest", "done");
  }

  async acceptApplicantOnChain(
    taskId: string,
    workerUserId: string,
    options?: { onStepChange?: (stepId: string, status: "pending" | "active" | "done" | "error") => void },
  ): Promise<void> {
    const step = options?.onStepChange;
    if (!this.polygonClient) throw new Error("Polygon client not available");

    step?.("accept_applicant", "active");
    const chainTaskId = await this.lookupChainTaskId(taskId);
    if (chainTaskId === null) throw new Error("Task not yet on-chain");

    const { data: wallet, error: walletErr } = await supabase.rpc("get_user_wallet", {
      p_user_id: workerUserId,
    });
    if (walletErr) throw new Error(`Failed to look up worker wallet: ${walletErr.message}`);
    if (!wallet) throw new Error("Worker has no wallet address");

    await this.polygonClient.withContext(
      { flow: "creatorAcceptedApplicant", taskId: chainTaskId },
      () => this.polygonClient!.acceptApplicant(BigInt(chainTaskId), wallet),
    );

    // Mark the task as on-chain assigned
    await supabase
      .from("tasks")
      .update({ chain_assigned: true, status: "assigned" })
      .eq("id", taskId);

    step?.("accept_applicant", "done");
  }

  // ─── Helpers ────────────────────────────────────────────

  /**
   * The single chokepoint every on-chain task call goes through, so the
   * cross-chain guard lives here. chain_task_id is a per-contract counter, so
   * the same id names a different task on each network -- and tasks inherit
   * their chain from the parent quest (`quests.chain_id`), hence the join.
   */
  /**
   * Resolve how a task settles: volunteer (no chain, no escrow) or paid.
   *
   * FAIL-CLOSED BY DESIGN (DEV-246 review). A task is treated as volunteer only
   * when the row was actually read and its budget is exactly 0. Any read failure
   * — RLS block, network blip, missing row — throws rather than defaulting,
   * because the "volunteer" answer is the one that SKIPS mandatory on-chain
   * settlement. Defaulting it open would let a transient DB error silently
   * strand a paid task's escrow. Mirrors the `?? -1` fail-closed default in
   * SupabaseQuestService.lockFundsAndStart.
   */
  private async loadTaskSettlement(
    taskId: string,
  ): Promise<{ isVolunteer: boolean; chainTaskId: number | null }> {
    const { data: taskRow, error } = await supabase
      .from("tasks")
      .select("chain_task_id, budget_usd, quests!inner(chain_id)")
      .eq("id", taskId)
      .single();
    if (error) {
      throw new Error(`Failed to load task settlement terms: ${error.message}`);
    }
    if (!taskRow) {
      throw new Error("Failed to load task settlement terms: no data returned (possible RLS block).");
    }
    const chainTaskId = (taskRow.chain_task_id as number | null) ?? null;
    // Same cross-chain guard lookupChainTaskId applies: only meaningful once
    // the task actually exists on-chain. Folded in here so submitWork and
    // reviewSubmission keep the protection they had before DEV-246 routed them
    // through their own bare select.
    if (chainTaskId !== null) {
      const quest = (taskRow as { quests?: { chain_id?: number | null } | { chain_id?: number | null }[] }).quests;
      const questChainId = Array.isArray(quest) ? quest[0]?.chain_id : quest?.chain_id;
      assertOnActiveChain(questChainId, "task");
    }
    return {
      isVolunteer: Number(taskRow.budget_usd) === 0,
      chainTaskId,
    };
  }

  private async lookupChainTaskId(taskId: string): Promise<number | null> {
    const { data, error } = await supabase
      .from("tasks")
      .select("chain_task_id, quests!inner(chain_id)")
      .eq("id", taskId)
      .single();
    // Distinguish "task exists, not yet on-chain" (data?.chain_task_id == null)
    // from "Supabase / RLS / network failed" so callers don't show a misleading
    // "task has not been funded on-chain yet" toast on a transient DB failure.
    if (error) {
      throw new Error(`Failed to look up task on-chain ID: ${error.message}`);
    }
    const chainTaskId = data?.chain_task_id ?? null;
    // Only meaningful once the task actually exists on-chain; a task with no
    // chain_task_id has nothing on either network to confuse.
    if (chainTaskId !== null) {
      const quest = (data as { quests?: { chain_id?: number | null } | { chain_id?: number | null }[] } | null)?.quests;
      const questChainId = Array.isArray(quest) ? quest[0]?.chain_id : quest?.chain_id;
      assertOnActiveChain(questChainId, "task");
    }
    return chainTaskId;
  }
}
